// HerbalSoapCertifications.js - Herbal soap themed Certifications & Awards section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a herbal soap themed Certifications & Awards section with credential cards
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the certifications section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The certifications section elements
 */
export const createHerbalSoapCertificationsElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const certificationsSectionClass = 'herbal-soap-certifications-section';
  const certificationsContainerClass = 'herbal-soap-certifications-container';
  const certificationsHeaderClass = 'herbal-soap-certifications-header';
  const certificationsTitleClass = 'herbal-soap-certifications-title';
  const certificationsSubtitleClass = 'herbal-soap-certifications-subtitle';
  const certificationsGridClass = 'herbal-soap-certifications-grid';
  const certificationCardClass = 'herbal-soap-certification-card';
  const certificationIconClass = 'herbal-soap-certification-icon';
  const certificationContentClass = 'herbal-soap-certification-content';
  const certificationHeaderClass = 'herbal-soap-certification-header';
  const certificationNameClass = 'herbal-soap-certification-name';
  const certificationDescClass = 'herbal-soap-certification-description';
  const certificationYearClass = 'herbal-soap-certification-year';
  const certificationBadgeClass = 'herbal-soap-certification-badge';
  const certificationsPatternOverlayClass = 'herbal-soap-certifications-pattern-overlay';

  // Desktop Styles - Herbal theme with natural green colors
  const certificationsSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f0fdf4',
    background: `
      radial-gradient(circle at 20% 30%, rgba(144, 238, 144, 0.15) 0%, transparent 50%),
      radial-gradient(circle at 80% 70%, rgba(34, 139, 34, 0.10) 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, rgba(26, 61, 26, 0.05) 0%, transparent 70%),
      linear-gradient(135deg, #f0fdf4 0%, #dcfce7 25%, #bbf7d0 50%, #dcfce7 75%, #f0fdf4 100%)
    `,
    color: '#1a3d1a',
    width: '100%',
    overflow: 'hidden'
  };

  const certificationsPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      radial-gradient(circle at 20% 30%, rgba(34, 139, 34, 0.04) 1px, transparent 1px),
      radial-gradient(circle at 80% 70%, rgba(144, 238, 144, 0.04) 1px, transparent 1px)
    `,
    backgroundSize: '80px 80px, 120px 120px',
    backgroundPosition: '0 0, 60px 60px',
    zIndex: '0',
    opacity: '0.6'
  };

  const certificationsContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const certificationsHeaderStyles = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const certificationsTitleStyles = {
    fontSize: '40px',
    fontWeight: '700',
    color: '#1a3d1a',
    marginBottom: '16px',
    fontFamily: '"Inter", sans-serif',
    letterSpacing: '-0.02em',
    lineHeight: '1.2'
  };

  const certificationsSubtitleStyles = {
    fontSize: '18px',
    color: '#4a5568',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.5',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400'
  };

  const certificationsGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '20px',
    marginTop: '50px'
  };

  const certificationCardStyles = {
    background: '#ffffff',
    border: '1px solid rgba(144, 238, 144, 0.2)',
    borderRadius: '12px',
    padding: '20px',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(34, 139, 34, 0.06)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px'
  };

  const certificationCardHoverStyles = {
    transform: 'translateY(-2px)',
    borderColor: '#90EE90',
    boxShadow: '0 8px 25px rgba(34, 139, 34, 0.12), 0 0 15px rgba(144, 238, 144, 0.1)'
  };

  const certificationIconStyles = {
    width: '50px',
    height: '50px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '12px',
    backgroundColor: 'rgba(144, 238, 144, 0.15)',
    fontSize: '24px',
    color: '#228b22',
    transition: 'all 0.3s ease',
    flexShrink: '0'
  };

  const certificationContentStyles = {
    flex: '1',
    display: 'flex',
    flexDirection: 'column'
  };

  const certificationHeaderStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px'
  };

  const certificationNameStyles = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1a3d1a',
    fontFamily: '"Inter", sans-serif',
    lineHeight: '1.3',
    margin: '0'
  };

  const certificationBadgeStyles = {
    display: 'inline-block',
    padding: '4px 8px',
    backgroundColor: '#228b22',
    background: 'linear-gradient(135deg, #228b22 0%, #90EE90 100%)',
    color: '#ffffff',
    borderRadius: '12px',
    fontSize: '9px',
    fontWeight: '600',
    fontFamily: '"Inter", sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    boxShadow: '0 2px 6px rgba(34, 139, 34, 0.2)',
    flexShrink: '0'
  };

  const certificationDescStyles = {
    fontSize: '13px',
    color: '#4a5568',
    lineHeight: '1.4',
    marginBottom: '8px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '400',
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  };

  const certificationYearStyles = {
    fontSize: '11px',
    fontWeight: '500',
    color: '#6b7280',
    fontFamily: '"Inter", sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    margin: '0'
  };

  // Tablet Styles
  const tabletCertificationsGridStyles = {
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '16px'
  };

  const tabletCertificationCardStyles = {
    padding: '16px',
    gap: '12px'
  };

  const tabletCertificationIconStyles = {
    width: '44px',
    height: '44px',
    fontSize: '20px'
  };

  const tabletCertificationsTitleStyles = {
    fontSize: '32px'
  };

  const tabletCertificationsSubtitleStyles = {
    fontSize: '16px'
  };

  // Mobile Styles
  const mobileCertificationsSectionStyles = {
    ...certificationsSectionStyles,
    paddingTop: '60px',
    paddingBottom: '60px'
  };

  const mobileCertificationsHeaderStyles = {
    marginBottom: '40px'
  };

  const mobileCertificationsTitleStyles = {
    fontSize: '28px'
  };

  const mobileCertificationsSubtitleStyles = {
    fontSize: '16px'
  };

  const mobileCertificationsGridStyles = {
    gridTemplateColumns: '1fr',
    gap: '12px'
  };

  const mobileCertificationCardStyles = {
    padding: '16px',
    gap: '12px',
    flexDirection: 'column',
    textAlign: 'center'
  };

  const mobileCertificationIconStyles = {
    width: '40px',
    height: '40px',
    fontSize: '18px',
    alignSelf: 'center'
  };

  const mobileCertificationHeaderStyles = {
    flexDirection: 'column',
    gap: '6px',
    alignItems: 'center'
  };

  const mobileCertificationNameStyles = {
    fontSize: '14px'
  };

  const mobileCertificationDescStyles = {
    fontSize: '12px',
    textAlign: 'center',
    WebkitLineClamp: '2'
  };

  const mobileCertificationYearStyles = {
    fontSize: '10px'
  };

  const mobileCertificationBadgeStyles = {
    fontSize: '8px',
    padding: '3px 6px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(certificationsSectionClass, certificationsSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationsPatternOverlayClass, certificationsPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationsContainerClass, certificationsContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationsHeaderClass, certificationsHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationsTitleClass, certificationsTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationsSubtitleClass, certificationsSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationsGridClass, certificationsGridStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationCardClass, certificationCardStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationIconClass, certificationIconStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationContentClass, certificationContentStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationHeaderClass, certificationHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationNameClass, certificationNameStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationDescClass, certificationDescStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationYearClass, certificationYearStyles);
    newRegistry[DEVICES.DESKTOP].set(certificationBadgeClass, certificationBadgeStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${certificationCardClass}--hover`, certificationCardHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(certificationsGridClass, tabletCertificationsGridStyles);
    newRegistry[DEVICES.TABLET].set(certificationCardClass, tabletCertificationCardStyles);
    newRegistry[DEVICES.TABLET].set(certificationIconClass, tabletCertificationIconStyles);
    newRegistry[DEVICES.TABLET].set(certificationsTitleClass, tabletCertificationsTitleStyles);
    newRegistry[DEVICES.TABLET].set(certificationsSubtitleClass, tabletCertificationsSubtitleStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(certificationsSectionClass, mobileCertificationsSectionStyles);
    newRegistry[DEVICES.MOBILE].set(certificationsHeaderClass, mobileCertificationsHeaderStyles);
    newRegistry[DEVICES.MOBILE].set(certificationsTitleClass, mobileCertificationsTitleStyles);
    newRegistry[DEVICES.MOBILE].set(certificationsSubtitleClass, mobileCertificationsSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(certificationsGridClass, mobileCertificationsGridStyles);
    newRegistry[DEVICES.MOBILE].set(certificationCardClass, mobileCertificationCardStyles);
    newRegistry[DEVICES.MOBILE].set(certificationIconClass, mobileCertificationIconStyles);
    newRegistry[DEVICES.MOBILE].set(certificationContentClass, certificationContentStyles);
    newRegistry[DEVICES.MOBILE].set(certificationHeaderClass, mobileCertificationHeaderStyles);
    newRegistry[DEVICES.MOBILE].set(certificationNameClass, mobileCertificationNameStyles);
    newRegistry[DEVICES.MOBILE].set(certificationDescClass, mobileCertificationDescStyles);
    newRegistry[DEVICES.MOBILE].set(certificationYearClass, mobileCertificationYearStyles);
    newRegistry[DEVICES.MOBILE].set(certificationBadgeClass, mobileCertificationBadgeStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    certificationsSectionClass,
    certificationsPatternOverlayClass,
    certificationsContainerClass,
    certificationsHeaderClass,
    certificationsTitleClass,
    certificationsSubtitleClass,
    certificationsGridClass,
    certificationCardClass,
    certificationIconClass,
    certificationContentClass,
    certificationHeaderClass,
    certificationNameClass,
    certificationDescClass,
    certificationYearClass,
    certificationBadgeClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const patternOverlayId = sectionId + 1;
  const containerId = sectionId + 2;
  const headerId = sectionId + 3;
  const titleId = sectionId + 4;
  const subtitleId = sectionId + 5;
  const gridId = sectionId + 6;

  // Certifications data
  const certifications = [
    {
      icon: '🌿',
      name: 'USDA Organic Certified',
      description: 'Official United States Department of Agriculture certification for organic ingredients and production methods.',
      year: 'Certified Since 2020',
      badge: 'Organic'
    },
    {
      icon: '🏆',
      name: 'Natural Products Award',
      description: 'Excellence award from Natural Products Association for outstanding quality and innovation in herbal skincare.',
      year: 'Awarded 2023',
      badge: 'Award'
    },
    {
      icon: '♻️',
      name: 'Eco-Friendly Certified',
      description: 'Environmental certification for sustainable packaging and eco-conscious manufacturing processes.',
      year: 'Certified 2021',
      badge: 'Eco-Certified'
    },
    {
      icon: '🧪',
      name: 'Cruelty-Free Verified',
      description: 'Leaping Bunny certification ensuring no animal testing in product development or manufacturing.',
      year: 'Verified 2019',
      badge: 'Cruelty-Free'
    },
    {
      icon: '✨',
      name: 'Artisan Soap Guild',
      description: 'Professional membership in the Handcrafted Soap & Cosmetic Guild for traditional soap making excellence.',
      year: 'Member Since 2018',
      badge: 'Artisan'
    },
    {
      icon: '🌱',
      name: 'Fair Trade Certified',
      description: 'Fair Trade certification ensuring ethical sourcing of ingredients and fair compensation for farmers.',
      year: 'Certified 2022',
      badge: 'Fair Trade'
    }
  ];

  // Create certification card elements
  let currentId = gridId + 1;
  const certificationElements = [];

  certifications.forEach((cert, index) => {
    const cardId = currentId++;
    const iconId = currentId++;
    const contentId = currentId++;
    const headerId = currentId++;
    const nameId = currentId++;
    const badgeId = currentId++;
    const descId = currentId++;
    const yearId = currentId++;

    certificationElements.push(
      // Certification Card
      {
        id: cardId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${cert.name} Card`,
        parentId: gridId,
        hierarchyName: `${cert.name} Card`,
        className: certificationCardClass,
        styles: {},
        children: [iconId, contentId],
        text: '',
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-certification-card'
        }
      },
      // Icon
      {
        id: iconId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${cert.name} Icon`,
        parentId: cardId,
        hierarchyName: `${cert.name} Icon`,
        className: certificationIconClass,
        styles: {},
        children: [],
        text: cert.icon,
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-certification-icon'
        }
      },
      // Content Container
      {
        id: contentId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${cert.name} Content`,
        parentId: cardId,
        hierarchyName: `${cert.name} Content`,
        className: certificationContentClass,
        styles: {},
        children: [headerId, descId, yearId],
        text: '',
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-certification-content'
        }
      },
      // Header (Name + Badge)
      {
        id: headerId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${cert.name} Header`,
        parentId: contentId,
        hierarchyName: `${cert.name} Header`,
        className: certificationHeaderClass,
        styles: {},
        children: [nameId, badgeId],
        text: '',
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-certification-header'
        }
      },
      // Name
      {
        id: nameId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'h3',
        name: `${cert.name} Name`,
        parentId: headerId,
        hierarchyName: `${cert.name} Name`,
        className: certificationNameClass,
        styles: {},
        children: [],
        text: cert.name,
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-certification-name'
        }
      },
      // Badge
      {
        id: badgeId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `${cert.name} Badge`,
        parentId: headerId,
        hierarchyName: `${cert.name} Badge`,
        className: certificationBadgeClass,
        styles: {},
        children: [],
        text: cert.badge,
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-certification-badge'
        }
      },
      // Description
      {
        id: descId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'p',
        name: `${cert.name} Description`,
        parentId: contentId,
        hierarchyName: `${cert.name} Description`,
        className: certificationDescClass,
        styles: {},
        children: [],
        text: cert.description,
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-certification-description'
        }
      },
      // Year
      {
        id: yearId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `${cert.name} Year`,
        parentId: contentId,
        hierarchyName: `${cert.name} Year`,
        className: certificationYearClass,
        styles: {},
        children: [],
        text: cert.year,
        visibility: 'visible',
        isExpanded: true,
        attributes: {
          'data-element': 'herbal-soap-certification-year'
        }
      }
    );
  });

  // Get certification card IDs for the grid
  const certificationCardIds = certificationElements
    .filter(element => element.parentId === gridId)
    .map(element => element.id);

  // Create base elements
  const baseElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Herbal Soap Certifications Section',
      parentId: null,
      hierarchyName: 'Herbal Soap Certifications Section',
      className: certificationsSectionClass,
      styles: {},
      children: [patternOverlayId, containerId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-certifications-section',
        'data-component': 'section',
        'data-section-type': 'certifications',
        'data-page-id': currentPageId
      }
    },
    
    // Pattern overlay
    {
      id: patternOverlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Pattern Overlay',
      parentId: sectionId,
      hierarchyName: 'Pattern Overlay',
      className: certificationsPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-certifications-pattern-overlay'
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certifications Container',
      parentId: sectionId,
      hierarchyName: 'Certifications Container',
      className: certificationsContainerClass,
      styles: {},
      children: [headerId, gridId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-certifications-container'
      }
    },
    
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certifications Header',
      parentId: containerId,
      hierarchyName: 'Certifications Header',
      className: certificationsHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-certifications-header'
      }
    },
    
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Certifications Title',
      parentId: headerId,
      hierarchyName: 'Certifications Title',
      className: certificationsTitleClass,
      styles: {},
      children: [],
      text: 'Certifications & Awards',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-certifications-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Certifications Subtitle',
      parentId: headerId,
      hierarchyName: 'Certifications Subtitle',
      className: certificationsSubtitleClass,
      styles: {},
      children: [],
      text: 'Our commitment to quality, sustainability, and ethical practices is recognized through these prestigious certifications and industry awards.',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-certifications-subtitle'
      }
    },
    
    // Grid
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Certifications Grid',
      parentId: containerId,
      hierarchyName: 'Certifications Grid',
      className: certificationsGridClass,
      styles: {},
      children: certificationCardIds,
      text: '',
      visibility: 'visible',
      isExpanded: true,
      attributes: {
        'data-element': 'herbal-soap-certifications-grid'
      }
    }
  ];

  // Combine all elements
  const allElements = [...baseElements, ...certificationElements];
  
  // Return the created elements
  return {
    elements: allElements
  };
}; 